import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { usePhoneNumber } from '../context/PhoneContext';
import { setJsonLd, setPageSeo } from '../utils/seo';
import './FAQPage.css';

const getFaqs = (phoneDisplay) => [
  {
    category: "Getting Started",
    questions: [
      {
        q: "What is Binding Thread?",
        a: `Binding Thread is a keepsake letter collection service that turns your loved one\u2019s spoken memories into beautifully crafted keepsake letters. Your parent or loved one simply calls our free number \u2014 ${phoneDisplay} \u2014 and shares their stories aloud. We then transcribe those spoken moments into keepsake letters that are mailed directly to you, letter by letter, until your collection is complete.`
      },
      {
        q: "How does it work?",
        a: `It\u2019s simple: your loved one dials ${phoneDisplay}, speaks their memory aloud, and hangs up. We listen to every word, gently transcribe it, and craft it into a keepsake letter on archival-quality paper. The finished letter is then posted to you. Letter by letter, your collection grows into a treasured keepsake your family will hold for generations.`
      },
      {
        q: "Who is Binding Thread for?",
        a: "Binding Thread is designed for adults who want to preserve the stories of a parent, grandparent, or elderly loved one. It\u2019s perfect for families where the storyteller isn\u2019t tech-savvy, lives far away, or is in residential care. If you\u2019ve ever wished you could capture their memories before it\u2019s too late \u2014 this is for you."
      },
      {
        q: "Does my loved one need a smartphone or internet?",
        a: "No. That\u2019s the beauty of it. All they need is any telephone \u2014 landline or mobile. No apps, no screens, no internet required. We call it the Zero-Tech Guarantee."
      },
      {
        q: "How many letters do I receive?",
        a: "Each Binding Thread collection includes a set number of letters: A Thread (3 letters), Woven (6 letters), or Tapestry (12 letters). Letters arrive as your loved one shares their stories \u2014 there is no schedule and no monthly billing, just a beautiful keepsake collection that builds at their pace."
      },
      {
        q: "How is Binding Thread different from digital memory services?",
        a: "Most memory services rely on apps, video recordings, or digital platforms that require your loved one to navigate technology. Binding Thread removes all of that. There\u2019s no screen, no login, no internet required \u2014 just a phone call. And instead of storing memories as digital files that can be lost or forgotten on a hard drive, we turn them into keepsake letters on archival paper \u2014 something physical you can hold, display, and pass down for generations."
      },
      {
        q: "Can someone in a nursing home or hospital use Binding Thread?",
        a: "Absolutely. That\u2019s one of the reasons we built it this way. All your loved one needs is access to a telephone \u2014 landline or mobile. They can make a free call from their bed, their favourite chair, or anywhere they feel comfortable. No visitors, technology, or setup required. It\u2019s an ideal way to capture stories from someone in care, even if you can\u2019t be there in person."
      }
    ]
  },
  {
    category: "The Phone Call",
    questions: [
      {
        q: "What number does my loved one call?",
        a: `They call ${phoneDisplay}. It\u2019s a free call, available anytime \u2014 day or night.`
      },
      {
        q: "How long can each recording be?",
        a: "There\u2019s no time limit. Your loved one can share as much or as little as they like, at their own pace. No rush, no pressure."
      },
      {
        q: "What if my loved one forgets to call?",
        a: "Don\u2019t worry \u2014 there is no time limit on your collection. Letters wait for them. We send a gentle reminder after a quiet period, and we\u2019re always here if you\u2019d like to talk."
      },
      {
        q: "Can my loved one call whenever they want?",
        a: "Absolutely. There\u2019s no schedule and no pressure. They can call whenever a memory strikes \u2014 morning, noon, or night."
      }
    ]
  },
  {
    category: "The Collections",
    questions: [
      {
        q: "How much does it cost?",
        a: "Binding Thread is sold as three keepsake letter collections: A Thread (3 letters) for $149 AUD, Woven (6 letters) for $249 AUD, and Tapestry (12 letters) for $429 AUD. The keepsake tin and shipping are included in every collection. There is no monthly billing \u2014 you pay once and your collection is yours."
      },
      {
        q: "Which collection should I choose?",
        a: "A Thread is the perfect place to begin if you\u2019re unsure or want to start with the most important memories. Woven is the most loved \u2014 enough room for stories to breathe. Tapestry is for the family who wants a complete legacy, the full picture of a life. You can begin with any collection that feels right."
      },
      {
        q: "Is there a subscription or recurring charge?",
        a: "No. Binding Thread collections are a single one-time purchase. No monthly billing, no recurring charges, no cancellation needed."
      },
      {
        q: "Can I purchase another collection later?",
        a: "Of course. Many families return for a second collection once they\u2019ve experienced the joy of the first. Simply visit our Shop and choose your next collection whenever you\u2019re ready."
      }
    ]
  },
  {
    category: "The Keepsake Tin",
    questions: [
      {
        q: "What is the Keepsake Tin?",
        a: "The Keepsake Tin is a beautiful, heirloom-style storage tin designed to hold your Binding Thread letters. It arrives with your first letter \u2014 a beautiful home for everything that follows. It\u2019s included in every collection."
      },
      {
        q: "Is the tin included in every collection?",
        a: "Yes. The keepsake tin is included with A Thread, Woven and Tapestry collections at no extra cost \u2014 along with shipping on all letters."
      }
    ]
  },
  {
    category: "Shipping & Delivery",
    questions: [
      {
        q: "How are the letters delivered?",
        a: "All letters are posted via Australia Post directly to the address you provide during onboarding. The keepsake tin arrives in the post with your first letter."
      },
      {
        q: "Is shipping included?",
        a: "Yes. Shipping is included in every Binding Thread collection \u2014 the price you see is the price you pay."
      },
      {
        q: "Is Binding Thread available outside Australia?",
        a: `Yes! We now serve customers in Australia and the United States. Australian callers use our free 1800 number, and US callers have a dedicated toll-free number at ${phoneDisplay}. Letters are posted to your door regardless of which country you\u2019re in. If you\u2019re in another country and interested, contact us at admin@bindingthread.com.au \u2014 we\u2019d love to help.`
      }
    ]
  },
  {
    category: "Refunds & Issues",
    questions: [
      {
        q: "What if my loved one doesn\u2019t want to participate?",
        a: "If you purchase a collection and your loved one decides they don\u2019t wish to participate before any letters have been produced, we offer a full refund within the first 14 days. Simply email admin@bindingthread.com.au and we\u2019ll take care of it."
      },
      {
        q: "What if my loved one doesn\u2019t call?",
        a: "There is no time limit on your collection. Your letters wait for them. We\u2019ll send a gentle reminder if it feels right, and we\u2019re always here if you\u2019d like to talk."
      },
      {
        q: "What if a letter or tin arrives damaged?",
        a: "If anything arrives damaged due to postal handling, simply email a photo to admin@bindingthread.com.au within 7 days. We\u2019ll send a replacement at no extra cost."
      },
      {
        q: "Can I get a refund after my collection has shipped?",
        a: "Due to the nature of the product, we don\u2019t offer change-of-mind refunds once your collection has been shipped. However, damaged items are always replaced free of charge."
      }
    ]
  },
  {
    category: "Privacy & Security",
    questions: [
      {
        q: "What happens to the recordings?",
        a: "Once your loved one\u2019s call is received, the audio is securely stored in an encrypted cloud environment. Our team listens to the recording, carefully transcribes it, and crafts it into a keepsake letter. The audio is retained securely in case you ever want a re-issue or additional copy. We never share, sell, or use recordings for any purpose other than creating your letters."
      },
      {
        q: "Who hears my loved one\u2019s recordings?",
        a: "Only authorised staff \u2014 the founder and designated scribes \u2014 have access to audio files, solely for the purpose of transcription and quality control."
      },
      {
        q: "Do you sell our data or stories?",
        a: "Never. We never sell your data, stories, or voice recordings to third parties or AI training sets. All audio files are stored in encrypted cloud environments."
      },
      {
        q: "Can I request my data be deleted?",
        a: "Yes. You can request the deletion of your audio files or personal data at any time by contacting us at admin@bindingthread.com.au."
      },
      {
        q: "Who owns the stories?",
        a: "You and your loved one retain all intellectual property rights to the stories shared. Binding Thread is granted a limited licence to process the content solely for fulfilling the service."
      }
    ]
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`} data-testid="faq-item">
      <button className="faq-question" onClick={() => setIsOpen(!isOpen)} data-testid="faq-question">
        <span>{question}</span>
        <ChevronDown size={20} className={`faq-chevron ${isOpen ? 'rotated' : ''}`} />
      </button>
      <div className={`faq-answer ${isOpen ? 'faq-answer-open' : 'faq-answer-closed'}`} data-testid="faq-answer">
        <p>{answer}</p>
      </div>
    </div>
  );
}

function FAQPage() {
  const phone = usePhoneNumber();
  const faqs = getFaqs(phone.display);

  useEffect(() => {
    setPageSeo({
      title: 'FAQs | Keepsake Letters \u2014 Binding Thread',
      description: 'Common questions about Binding Thread\u2019s keepsake letter collections. Learn how it works, our three collections (A Thread, Woven, Tapestry) from $149, what\u2019s included, shipping, refunds, and our zero-tech phone call process.',
      canonical: 'https://www.bindingthread.com.au/faq',
      ogImage: 'https://www.bindingthread.com.au/logo-final.png'
    });
    const allQuestions = faqs.flatMap(cat => cat.questions);
    setJsonLd('ld-faq', {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": allQuestions.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a }
      }))
    });
  }, []);

  return (
    <div className="faq-page" data-testid="faq-page">
      <section className="faq-hero">
        <div className="container">
          <h1>Frequently Asked Questions</h1>
          <p className="faq-hero-intro">
            Binding Thread turns your loved one&apos;s spoken memories into beautifully crafted keepsake letters, delivered as a complete collection. Below you&apos;ll find answers to the most common questions about our service, from how the phone call works to our three collections, shipping, and privacy. If you can&apos;t find what you&apos;re looking for, reach out to us anytime.
          </p>
        </div>
      </section>

      <section className="faq-content">
        <div className="container">
          {faqs.map((category, i) => (
            <div key={i} className="faq-category" data-testid="faq-category">
              <h2>{category.category}</h2>
              <div className="faq-list">
                {category.questions.map((item, j) => (
                  <FAQItem key={j} question={item.q} answer={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="faq-cta">
        <div className="container text-center">
          <h2>Still have questions?</h2>
          <p>
            Get in touch at{' '}
            <a href="mailto:admin@bindingthread.com.au">admin@bindingthread.com.au</a>
          </p>
          <Link to="/shop" className="btn btn-urgent" data-testid="faq-cta-btn">
            Start Your Subscription
          </Link>
          <div className="faq-cross-links">
            <Link to="/how-it-works" className="inline-link">See how it works</Link>
            <span className="link-divider">&middot;</span>
            <Link to="/about" className="inline-link">About Binding Thread</Link>
            <span className="link-divider">&middot;</span>
            <Link to="/terms" className="inline-link">Terms &amp; Conditions</Link>
            <span className="link-divider">&middot;</span>
            <Link to="/privacy" className="inline-link">Privacy Policy</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FAQPage;
